
import Vue from 'vue';
import {
  VideoModel,
  ListItemModel,
  ListModel,
  UserModel,
  CourseModel,
  ClassroomModel,
} from '@descomplica/dex-models';
import NextContent from './NextContent.vue';
import Reactions from './reactions/Reactions.vue';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { BaseTrackType, TrackLessonReactedType } from '@/helpers/analytics/types';
import { getPaddedDisplayNumber } from '@/helpers/string';

import { ANALYTICS_LABELS } from '@/helpers/listItems';
import { getCourseCategory } from '@/helpers/category';
import NotesTimelinePlayer from './NotesTimelinePlayer.vue';
import { NoteModel } from '@/models/student-notes/note.model';
import { LIVE_TYPE } from '@/helpers/analytics/eventProperties';
import { WeekModel } from '@/models/studyPlanning/WeekModel';
import { StudyPlanningModel } from '@/models/studyPlanning/StudyPlanningModel';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import { PostgradTrialStatus } from '@/models/postgradTrial/PostgradTrialModel';

// declaring Global var for KalturaPlayer
// eslint-disable-next-line no-var
declare var KalturaPlayer: any;

interface mediaData {
  entryId: string;
}

interface ActiveTracks {
  text?: { language: string };
}

interface KalturaPlayerInstance {
  pause: () => void;
  loadMedia: (_: mediaData) => void;
  ready: () => void;
  play: () => void;
  addEventListener: (a: any, b: (_?: any) => void) => void;
  getActiveTracks: () => ActiveTracks;
  duration: number;
  currentTime: number;
  playbackRate: number;
  Event: any;
}

interface Data {
  videoInstance: KalturaPlayerInstance | null;
  showOverlay: boolean;
  hasEmbed: boolean;
  uiVisible: boolean;
  currentIndex: number;
  totalItems: number;
}

export default Vue.extend({
  components: {
    Reactions,
    NextContent,
    NotesTimelinePlayer,
  },
  props: {
    user: {
      type: Object as Vue.PropType<UserModel>,
      default: null,
    },
    course: {
      type: Object as Vue.PropType<CourseModel>,
      default: null,
    },
    classroom: {
      type: Object as Vue.PropType<ClassroomModel>,
      default: null,
    },
    list: {
      type: Object as Vue.PropType<ListModel>,
      default: null,
    },
    video: {
      type: Object as Vue.PropType<VideoModel>,
      default: null,
    },
    nextList: {
      type: Object as Vue.PropType<ListModel>,
      default: null,
    },
    nextListItem: {
      type: Object as Vue.PropType<ListItemModel>,
      default: null,
    },
    hasAutoPlay: {
      type: Boolean,
      default: false,
    },
    hasReactions: {
      type: Boolean,
      default: false,
    },
    hideKalturaSpinner: {
      type: Boolean,
      default: false,
    },
    isLesson: {
      type: Boolean,
      default: false,
    },
    liveName: {
      type: String,
      default: '',
    },
    videoNotes: {
      type: Array as Vue.PropType<NoteModel[]>,
      required: true,
    },
    loopVideo: {
      type: Boolean,
      default: false,
    },
    playbackRate: {
      type: Number,
      default: 0,
    },
    targetId: {
      type: String,
      default: 'video',
    },
    volume: {
      type: Number,
      default: 1,
    },
  },
  data(): Data {
    return {
      hasEmbed: false,
      showOverlay: false,
      videoInstance: null,
      uiVisible: true,
      currentIndex: 0,
      totalItems: 0,
    };
  },
  computed: {
    isPostgraduateCourse(): boolean {
      return this.$store.getters.isPostgraduateCourse;
    },
    paddedIndex(): string {
      return getPaddedDisplayNumber(this.currentIndex + 1);
    },
    paddedTotalItems(): string {
      return getPaddedDisplayNumber(this.totalItems);
    },
    videoName(): string {
      return this.video ? this.video.name : '';
    },
    videoDuration(): number {
      return this.video?.duration || 0;
    },
    userCanViewPostgradStudyPlanning(): boolean {
      return this.$store.getters.canViewPostgradStudyPlanning;
    },
    studyPlanning(): StudyPlanningModel {
      return this.$store.getters.studyPlanning(this.$store.getters.currentCourse.id);
    },
    userHasActivePostgradStudyPlanning(): boolean {
      return Boolean(this.studyPlanning);
    },
    totalAnticipatedStudyPlanningWeeks(): number {
      const currentWeek: WeekModel = this.$store.getters.studyPlanningWeek;

      if (!currentWeek) return 0;

      const anticipatedWeeks = this.$store.getters.studyPlanningAnticipatedWeeks(
        this.studyPlanning.id,
      ) as WeekModel[];

      if (!anticipatedWeeks.length) return 0;

      const highestAnticipatedWeekPosition = Math.max(...anticipatedWeeks.map((w) => w.position));

      return highestAnticipatedWeekPosition - currentWeek.position;
    },
    postgradStudyPlanningTrackingProps(): Record<string, boolean | number> {
      return {
        postgrad_study_planning_feature_enabled: this.userCanViewPostgradStudyPlanning,
        has_postgrad_study_planning: this.userHasActivePostgradStudyPlanning,
        current_total_anticipated_study_planning_weeks: this.totalAnticipatedStudyPlanningWeeks,
      };
    },
    defaultTrackingProps(): BaseTrackType {
      return {
        course: this.course.name,
        discipline: this.classroom.name,
        category: getCourseCategory(this.course),
        content_type: this.isLesson ? ANALYTICS_LABELS.video : ANALYTICS_LABELS.tutoring,
      };
    },
    videoNotesWithoutDraft(): NoteModel[] {
      return this.videoNotes?.filter((note) => !note.draft);
    },
    currentChimeraCourse(): ChimeraCourseModel | null {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    getTrialStatusByDescomplicaCourseId(): PostgradTrialStatus {
      return this.$store.getters.getTrialStatusByDescomplicaCourseId(
        this.currentChimeraCourse?._id,
      );
    },
  },
  watch: {
    async video(newVideo: VideoModel, oldVideo: VideoModel): Promise<void> {
      const newVideoAux = newVideo || {};
      const oldVideoAux = oldVideo || {};

      if (!this.hasEmbed) {
        await this.kalturaSetup();
        return;
      }

      if (!this.videoInstance) return;

      if (!newVideoAux.providerId) {
        this.videoInstance.pause();
      } else if (newVideoAux.providerId !== oldVideoAux.providerId) {
        this.showOverlay = false;
        this.videoInstance.loadMedia({ entryId: newVideoAux.providerId });
      }
      this.setIndexAndTotal(newVideo);
      this.trackLessonPlayed(false);
    },
  },
  async mounted() {
    if (this.video?.providerId && !this.hasEmbed) {
      await this.kalturaSetup();
    }

    if (!this.isLesson) {
      this.trackLiveConsumed();
    }
  },
  methods: {
    trackLiveConsumed(): void {
      Analytics.trackEvent(EVENT_LABELS.contentConsumed, {
        ...this.defaultTrackingProps,
        live_name: this.liveName,
        live_type: LIVE_TYPE.vod,
        ...this.getTrialStatusByDescomplicaCourseId,
      });
    },
    trackLessonPlayed(rewatch: boolean): void {
      if (this.video) {
        const videoName = this.video.name || 'no-name-defined';
        const tracks = this.videoInstance?.getActiveTracks();
        const textTrack = tracks?.text;

        Analytics.trackEvent(EVENT_LABELS.lessonPlayed, {
          ...this.defaultTrackingProps,
          lesson_name: this.list.name,
          lesson_slug: this.list.slug,
          player_version: 'kaltura-v7',
          video: videoName,
          rewatch,
          subtitle: !!textTrack && textTrack.language !== 'off',
          ...(this.isPostgraduateCourse && this.postgradStudyPlanningTrackingProps),
          ...this.getTrialStatusByDescomplicaCourseId,
        });
      }
    },
    toggleOverlay(): void {
      this.showOverlay = !this.showOverlay;
    },
    createNewInstance(): KalturaPlayerInstance {
      /* eslint-disable */
      // no-undef
      // @ts-ignore
      return KalturaPlayer.setup({
        targetId: this.targetId,
        provider: {
          partnerId: 1612851,
          uiConfId: 47228233,
        },
        playback: {
          autoplay: this.hasAutoPlay,
          playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
          loop: this.loopVideo,
          volume: this.volume,
        },
        plugins: {
          dualscreen: {
            position: 'bottom-right',
            layout: 'SingleMedia',
            inverse: false,
            childSizePercentage: 30,
          },
        },
      });
      /* eslint-enable */
    },
    setIndexAndTotal(video: VideoModel): void {
      if (!video || !this.list || !this.list.listItems.length) return;
      const videoList = this.list.listItems.filter((item) => item.isVideo());

      this.totalItems = videoList.length;
      this.currentIndex = videoList.findIndex((item) => item.video.id === video.id);
    },
    async kalturaSetup() {
      if (!this.video) {
        return;
      }

      this.setIndexAndTotal(this.video);
      this.videoInstance = this.createNewInstance();

      if (this.videoInstance) {
        this.hasEmbed = true;
        await this.videoInstance.loadMedia({ entryId: this.video.providerId });
        await this.videoInstance.ready();

        this.onPlayerReady();
      }
    },
    onPlayerReady(): void {
      if (!this.videoInstance) return;

      this.videoInstance.addEventListener(
        this.videoInstance.Event.Core.FIRST_PLAY,
        this.getStoredPlayerSpeed,
      );
      this.videoInstance.addEventListener(
        this.videoInstance.Event.UI.USER_SELECTED_SPEED,
        this.storePlayerSpeed,
      );
      this.videoInstance.addEventListener(this.videoInstance.Event.Core.ENDED, this.onVideoEnd);
      this.videoInstance.addEventListener(
        this.videoInstance.Event.UI.UI_VISIBILITY_CHANGED,
        this.onUIVisibilityChanged,
      );
    },
    onReplay(): void {
      if (!this.videoInstance) return;
      this.videoInstance.play();
      this.toggleOverlay();

      this.trackLessonPlayed(true);
    },
    onGoToNextListItem(): void {
      this.$emit('onNextListClick');
      this.toggleOverlay();
    },
    onVideoEnd(): void {
      this.toggleOverlay();
      if (!this.videoInstance) return;
      this.$emit('onVideoEnded');
    },
    onUIVisibilityChanged(event: any): void {
      this.uiVisible = event.payload.visible;
    },
    getReactionsTrackingData(): TrackLessonReactedType | null {
      return (
        this.videoInstance && {
          ...this.defaultTrackingProps,
          video_timestamp: this.videoInstance?.currentTime,
          lesson_name: this.list.name,
          lesson_slug: this.list.slug,
          teacher:
            this.classroom && this.classroom.teachers.length
              ? this.classroom.teachers[0].name
              : null,
          video: this.video ? this.video.id : null,
          videoDuration: this.videoInstance?.duration,
        }
      );
    },
    storePlayerSpeed(eventData: { payload: { speed: number } }): void {
      localStorage.setItem('kaltura-player-playback-rate', eventData.payload.speed.toString());
    },
    getStoredPlayerSpeed(): void {
      if (this.playbackRate) {
        this.videoInstance!.playbackRate = this.playbackRate;
      } else {
        const savedPlayerSpeed = localStorage.getItem('kaltura-player-playback-rate');
        if (savedPlayerSpeed) {
          this.videoInstance!.playbackRate = Number(savedPlayerSpeed);
        }
      }
    },
    getCurrentTime(): number | undefined {
      return this.videoInstance?.currentTime;
    },
    pause(): void {
      return this.videoInstance?.pause();
    },
  },
});
